import type {
  CreateUpstreamRequest,
  CreateRouteBindingRequest,
  CreateRouteBindingResponse,
  CreateUpstreamResponse,
  DeleteRouteBindingResponse,
  DeleteUpstreamResponse,
  DiscoverUpstreamCapabilitiesRequest,
  DiscoverUpstreamCapabilitiesResponse,
  GetUpstreamResponse,
  ListRouteBindingsResponse,
  ListProbeResultsResponse,
  ListUpstreamChannelCostsResponse,
  ListUpstreamCapabilitiesResponse,
  ProbeUpstreamCapabilityResponse,
  ListUpstreamsResponse,
  ProbeRequest,
  ProbeResponse,
  ProbeResultRecord,
  UpdateUpstreamRequest,
  UpdateUpstreamResponse,
  UpdateRouteBindingRequest,
  UpdateRouteBindingResponse,
  UpdateUpstreamCapabilityResponse,
  SetUpstreamChannelCostsRequest,
  SetUpstreamChannelCostsResponse,
  RouteBindingRecord,
  UpstreamModelCapabilityRecord,
  UpstreamCatalogResponse,
  UpstreamConfig,
  UpstreamRetirementImpactResponse,
  UpstreamChannelCostRecord,
} from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers around `ops-api /v1/ops/upstreams/*`.
 *
 * Every call reuses `opsApiRequest`, so session cookie handling, the
 * ADR-004 error envelope, and network-error normalization land in one
 * place. React Query hooks (see `useUpstreams.ts`) layer caching and
 * invalidation on top of these primitives.
 *
 * All wire types come from `@nadoutong/contracts` — ops-api proxies
 * gateway's `/internal/upstreams/*` responses unchanged, so the shapes
 * are identical across the three layers.
 */

const BASE_PATH = '/v1/ops/upstreams';

export async function listUpstreams(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamConfig[]> {
  const response = await opsApiRequest<ListUpstreamsResponse>(
    `${BASE_PATH}`,
    { signal },
    fetchImpl,
  );
  return response.upstreams;
}

/**
 * Fetch the per-upstream model mapping catalog. Gateway filters out
 * soft-deleted upstreams from this query, so a just-deleted upstream will
 * briefly disappear from the response while its detail page still renders
 * from the direct GET.
 */
export async function listCatalog(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamCatalogResponse> {
  return opsApiRequest<UpstreamCatalogResponse>(
    `${BASE_PATH}/catalog`,
    { signal },
    fetchImpl,
  );
}

export async function getUpstream(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamConfig> {
  const response = await opsApiRequest<GetUpstreamResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}`,
    { signal },
    fetchImpl,
  );
  return response.upstream;
}

export async function listUpstreamChannelCosts(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamChannelCostRecord[]> {
  const response = await opsApiRequest<ListUpstreamChannelCostsResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/channel-costs`,
    { signal },
    fetchImpl,
  );
  return [...response.costs];
}

export async function setUpstreamChannelCosts(
  id: string,
  body: SetUpstreamChannelCostsRequest,
  fetchImpl: typeof fetch,
): Promise<UpstreamChannelCostRecord[]> {
  const response = await opsApiRequest<SetUpstreamChannelCostsResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/channel-costs`,
    { method: 'PUT', body },
    fetchImpl,
  );
  return [...response.costs];
}

/**
 * Retirement impact summary for the DangerZone confirmation dialog
 * (Epic #109 / P0.1). ops-api merges the gateway-owned slice (models
 * + traffic) with the platform-owned slice (funding batches) before
 * returning; we treat the merged payload as the single contract.
 */
export async function getUpstreamRetirementImpact(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamRetirementImpactResponse> {
  return opsApiRequest<UpstreamRetirementImpactResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/retirement-impact`,
    { signal },
    fetchImpl,
  );
}

export async function createUpstream(
  body: CreateUpstreamRequest,
  fetchImpl: typeof fetch,
): Promise<UpstreamConfig> {
  const response = await opsApiRequest<CreateUpstreamResponse>(
    `${BASE_PATH}`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.upstream;
}

export async function updateUpstream(
  id: string,
  body: UpdateUpstreamRequest,
  fetchImpl: typeof fetch,
): Promise<UpstreamConfig> {
  const response = await opsApiRequest<UpdateUpstreamResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
  return response.upstream;
}

export async function deleteUpstream(
  id: string,
  fetchImpl: typeof fetch,
): Promise<DeleteUpstreamResponse> {
  return opsApiRequest<DeleteUpstreamResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function probeUpstream(
  id: string,
  body: ProbeRequest,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ProbeResponse> {
  return opsApiRequest<ProbeResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/probe`,
    { method: 'POST', body, signal },
    fetchImpl,
  );
}

export async function listProbeResults(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
  limit?: number,
): Promise<ProbeResultRecord[]> {
  const query = limit !== undefined ? `?limit=${encodeURIComponent(limit)}` : '';
  const response = await opsApiRequest<ListProbeResultsResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/probes${query}`,
    { signal },
    fetchImpl,
  );
  return response.probes;
}

export async function listUpstreamCapabilities(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamModelCapabilityRecord[]> {
  const response = await opsApiRequest<ListUpstreamCapabilitiesResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities`,
    { signal },
    fetchImpl,
  );
  return [...response.capabilities];
}

export async function discoverUpstreamCapabilities(
  id: string,
  body: DiscoverUpstreamCapabilitiesRequest,
  fetchImpl: typeof fetch,
): Promise<DiscoverUpstreamCapabilitiesResponse> {
  return opsApiRequest<DiscoverUpstreamCapabilitiesResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/discover`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function probeUpstreamCapability(
  id: string,
  capabilityId: string,
  fetchImpl: typeof fetch,
): Promise<ProbeUpstreamCapabilityResponse> {
  return opsApiRequest<ProbeUpstreamCapabilityResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/${encodeURIComponent(capabilityId)}/probe`,
    { method: 'POST', body: {} },
    fetchImpl,
  );
}

export async function ignoreUpstreamCapability(
  id: string,
  capabilityId: string,
  fetchImpl: typeof fetch,
): Promise<UpdateUpstreamCapabilityResponse> {
  return opsApiRequest<UpdateUpstreamCapabilityResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/${encodeURIComponent(capabilityId)}/ignore`,
    { method: 'POST', body: {} },
    fetchImpl,
  );
}

export async function unignoreUpstreamCapability(
  id: string,
  capabilityId: string,
  fetchImpl: typeof fetch,
): Promise<UpdateUpstreamCapabilityResponse> {
  return opsApiRequest<UpdateUpstreamCapabilityResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/${encodeURIComponent(capabilityId)}/unignore`,
    { method: 'POST', body: {} },
    fetchImpl,
  );
}

export async function listRouteBindings(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RouteBindingRecord[]> {
  const response = await opsApiRequest<ListRouteBindingsResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/route-bindings`,
    { signal },
    fetchImpl,
  );
  return [...response.route_bindings];
}

export async function createRouteBinding(
  id: string,
  body: CreateRouteBindingRequest,
  fetchImpl: typeof fetch,
): Promise<RouteBindingRecord> {
  const response = await opsApiRequest<CreateRouteBindingResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/route-bindings`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.route_binding;
}

export async function updateRouteBinding(
  id: string,
  bindingId: string,
  body: UpdateRouteBindingRequest,
  fetchImpl: typeof fetch,
): Promise<RouteBindingRecord> {
  const response = await opsApiRequest<UpdateRouteBindingResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/route-bindings/${encodeURIComponent(bindingId)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
  return response.route_binding;
}

export async function deleteRouteBinding(
  id: string,
  bindingId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteRouteBindingResponse> {
  return opsApiRequest<DeleteRouteBindingResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/route-bindings/${encodeURIComponent(bindingId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}
